import React, { ReactNode, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import AgentationDevtools from '@/components/AgentationDevtools';
import { getEnv, navigate, pageAgent } from '@/common/agents';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import RoboneoContext from '$/pages/RoboneoContext';
import { useOfficialStore, InitLoginConfig } from '$/store/useOfficialStore';
import { isMobile } from '$/utils';
import { NoSSR } from '@meitu/catalyze-next';
import CookieBanner from '@/components/CookieBanner';
import { useCommonStore } from '@/store/useCommonStore';
import { ensureTeamInfoForPurchase } from '@/domains/team/teamPurchaseGuard';
import type { VipMembershipMode } from '$/store/useVipModalStore';
import Navbar from '@/components/Navbar';
import { PageInfo } from '@/common/pageType';
import Header from '@/components/Header';
// import MetaPixelSDK from '@/components/MetaPixelSDK';
import { MetaPixelProvider } from '@/components/MetaPixelsdk';
import useChooseIPStore, { closeChooseIPModal } from '$/components/ChooseIpModal/chooseIPStore';
import styles from './index.module.scss';

/** 首页和侧栏共用一个 IP 选择弹窗，首次打开时才加载实现。 */
const AsyncChooseIpModal = dynamic(() => import('@/components/CreateProjectIpModal'), {
  ssr: false,
});

/** 共享布局统一承载 IP 选择弹窗；IP 列表与详情均不渲染侧栏。 */
function Layout({ children }: { children: ReactNode; }) {
  const isChooseIPModalOpen = useChooseIPStore((state) => state.isModalOpen);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const toLogin = useCommonStore((state) => state.toLogin);
  const currentPathname = useCommonStore((state) => state.currentPathname);
  // const currentCountryCode = useOfficialStore((state) => state.currentCountryCode);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const init = useCommonStore((state) => state.init);
  const initialized = useCommonStore((state) => state.initialized);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const { locationName } = pageAgent.getCurrentPage() || {};
  const isMyIpPage = locationName === 'myips' || locationName === 'myIpDetail';

  /** 离开当前页面时关闭选择弹窗，避免创建 IP 后仍遮挡目标页面。 */
  useEffect(() => () => {
    closeChooseIPModal();
  }, [currentPathname]);

  const loadVconsole = async () => {
    if (isMobile() && process.env.NEXT_PUBLIC_ENVIRONMENT !== 'release') {
      const VConsole = await import('vconsole');
      // eslint-disable-next-line no-new, new-cap
      new VConsole.default();
    }
  };

  useEffect(() => {
    InitLoginConfig({
      toLogin: async () => {
        await toLogin();
        initUserInfo();
      },
    });
    init();
    loadVconsole();
  }, []);

  /**
   * 团队版购买前确保已有团队；没有团队时创建默认团队并让 SDK 使用新团队上下文下单。
   */
  const handleCreateQrcodeBefore = async ({ membershipMode }: { membershipMode: VipMembershipMode; }) => {
    if (membershipMode !== 'team') {
      return true;
    }

    const teamInfo = await ensureTeamInfoForPurchase();
    if (!teamInfo) {
      message.error(languageStore.smartcatTranslationText(
        (v) => v.current_team_info_unavailable_refresh_retry,
      ));
      return false;
    }

    return true;
  };
  // 注册chat的参数

  useEffect(() => {
    if (
      initialized
      && !isUserLoggedIn
      && locationName
      && (locationName === 'studio' || locationName === 'ai_flow')
    ) {
      message.info('RoboNeo_Web_login_check_text');
      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
        replace: true,
      });
    }
  }, [locationName, initialized, isUserLoggedIn]);

  const [activeNav, setActiveNav] = useState<PageInfo['locationName']>();
  const [showHeader, setShowHeader] = useState(false);
  const [hiddenHeader, setHiddenHeader] = useState(false);

  useEffect(() => {
    if (currentPathname) {
      const pageInfo = pageAgent.getCurrentPage();
      // 详情采用独立任务导航；站点顶部栏与左侧导航分别控制。
      const showGlobalHeader = ['home', 'project', 'team_project', 'community', 'myips'].includes(pageInfo?.locationName || '');
      setShowHeader(
        showGlobalHeader,
      );
      setHiddenHeader(['case_detail', 'case_preview'].includes(pageInfo?.locationName || ''));
      setActiveNav(pageInfo?.locationName);
    }
  }, [currentPathname]);
  // useEffect(() => {
  //   // 初始化并收集参数
  //   const initializeAnalytics = async () => {
  //     // 等待 MetaPixelSDK 初始化
  //     await new Promise((resolve) => {
  //       const check = () => {
  //         if (window.MetaPixelSDK) {
  //           resolve();
  //         } else {
  //           setTimeout(check, 100);
  //         }
  //       };
  //       check();
  //     });

  //     // 收集 Facebook 参数
  //     const fbc = window.MetaPixelSDK.getFbc();
  //     if (fbc && window.SDK) {
  //       window.SDK.setCommonStatsParams('fb_fbc', fbc);
  //     }

  //     window.MetaPixelSDK.waitForFbqReady(() => {
  //       const fbp = window.MetaPixelSDK.getFbp();
  //       if (fbp && window.SDK) {
  //         window.SDK.setCommonStatsParams('fb_fbp', fbp);
  //       }
  //     });
  //   };

  //   initializeAnalytics();
  // }, []);

  return (
    <NoSSR>
      {getEnv().ENV !== 'release' && <AgentationDevtools />}
      {isOversea ? <CookieBanner /> : null}
      <div
        id="layout_container"
        className={styles.layout_container}
        // data-roboneo-theme={theme}
        style={{ background: isMyIpPage ? 'var(--background-homePage)' : 'var(--background-canvas)' }}
      >
        <MetaPixelProvider
          onReady={({ fbc, fbp }) => {
            if (fbc) window.mtstat?.setCommonParams({ fb_fbc: fbc });
            if (fbp) window.mtstat?.setCommonParams({ fb_fbp: fbp });
          }}
        >
          <RoboneoContext enableVip onCreateQrcodeBefore={handleCreateQrcodeBefore}>
            {/* 头部 */}
            {showHeader && !hiddenHeader && locationName !== 'myIpDetail' && <Header />}

            {/* 导航 */}
            {showHeader && activeNav && !isMyIpPage && (
            <Navbar activeNav={activeNav} setActiveNav={setActiveNav} />
            )}
            {/* Meta Pixel SDK 客户端组件 */}
            {/* <MetaPixelSDK /> */}
            {children as React.ReactNode}
            {isChooseIPModalOpen && <AsyncChooseIpModal />}
          </RoboneoContext>
        </MetaPixelProvider>
      </div>
    </NoSSR>
  );
}
export default Layout;
